import { Suspense, useEffect } from 'react';
import { useLoader, useThree } from '@react-three/fiber';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import * as THREE from 'three';
import { isTextureUploadReady } from '../../utils/textureUploadReady';

function SafeHdrEnvironmentInner({ files }: { files: string }) {
  const { gl, scene } = useThree();
  const texture = useLoader(RGBELoader, files);

  useEffect(() => {
    if (!isTextureUploadReady(texture)) {
      console.warn('[SafeHdrEnvironment] HDR texture not upload-ready; skipping IBL:', files);
      return;
    }

    const pmrem = new THREE.PMREMGenerator(gl);
    pmrem.compileEquirectangularShader();

    let envMap: THREE.Texture;
    try {
      envMap = pmrem.fromEquirectangular(texture).texture;
    } catch (error) {
      console.warn('[SafeHdrEnvironment] PMREM fromEquirectangular failed; skipping IBL:', files, error);
      pmrem.dispose();
      return;
    }

    pmrem.dispose();
    scene.environment = envMap;

    return () => {
      if (scene.environment === envMap) {
        scene.environment = null;
      }
      envMap.dispose();
    };
  }, [files, gl, scene, texture]);

  return null;
}

/** HDR IBL without drei `<Environment>` — guards PMREM when texture data is missing. */
export function SafeHdrEnvironment({ files }: { files: string }) {
  return (
    <Suspense fallback={null}>
      <SafeHdrEnvironmentInner files={files} />
    </Suspense>
  );
}
